import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { SkeletonTable } from '../common/SkeletonLoader';

// Extended user interface for the admin dashboard
interface ExtendedUser {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  role: 'student' | 'manager' | 'admin';
  status: 'active' | 'pending' | 'suspended' | 'inactive';
  createdAt: string;
  lastLogin?: string;
  subscription?: {
    plan: 'free' | 'premium' | 'enterprise';
    status: 'active' | 'inactive' | 'cancelled';
    expiresAt?: string;
  };
  coachingData?: {
    sessionsCompleted: number;
    goalsSet: number;
    progressScore: number;
  };
  company?: string;
  position?: string;
  phone?: string;
  approvedBy?: string;
  approvedAt?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<ExtendedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | 'student' | 'manager' | 'admin'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'pending' | 'suspended' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'email' | 'role' | 'status' | 'created'>('created');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { adminUser } = useAuth();

  const fetchUsers = async () => {
    if (!adminUser) return;
    
    setLoading(true);
    try {
      const allUsers: ExtendedUser[] = [];

      // Fetch regular users (students)
      try {
        const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const usersSnapshot = await getDocs(usersQuery);
        const regularUsers = usersSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            uid: doc.id,
            email: data.email || '',
            displayName: data.displayName || data.name || 'Unknown User',
            photoURL: data.photoURL,
            role: 'student' as const,
            status: data.status || 'active' as const,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
            lastLogin: data.lastLogin?.toDate?.()?.toISOString() || data.lastLogin,
            subscription: data.subscription,
            coachingData: data.coachingData
          };
        });
        allUsers.push(...regularUsers);
      } catch (error) {
        console.log('No users collection or error fetching users:', error);
      }

      // Fetch managers
      try {
        const managersQuery = query(collection(db, 'managers'), orderBy('createdAt', 'desc'));
        const managersSnapshot = await getDocs(managersQuery);
        const managerUsers = managersSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            uid: doc.id,
            email: data.email || '',
            displayName: data.displayName || 'Unknown Manager',
            photoURL: data.photoURL,
            role: 'manager' as const,
            status: data.status || 'active' as const,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
            lastLogin: data.lastLogin?.toDate?.()?.toISOString() || data.lastLogin,
            company: data.company,
            position: data.position,
            phone: data.phone,
            approvedBy: data.approvedBy,
            approvedAt: data.approvedAt?.toDate?.()?.toISOString() || data.approvedAt
          };
        });
        allUsers.push(...managerUsers);
      } catch (error) {
        console.log('No managers collection or error fetching managers:', error);
      }

      // Fetch admin users
      try {
        const adminsQuery = query(collection(db, 'admin-users'), orderBy('createdAt', 'desc'));
        const adminsSnapshot = await getDocs(adminsQuery);
        const adminUsers = adminsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            uid: doc.id,
            email: data.email || '',
            displayName: data.displayName || 'Unknown Admin',
            photoURL: data.photoURL,
            role: 'admin' as const,
            status: 'active' as const,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
            lastLogin: data.lastLogin?.toDate?.()?.toISOString() || data.lastLogin
          };
        });
        allUsers.push(...adminUsers);
      } catch (error) {
        console.log('No admin-users collection or error fetching admin users:', error);
      }

      setUsers(allUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [adminUser]);

  const filteredAndSortedUsers = users
    .filter(user => {
      const matchesSearch = user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (user.company && user.company.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
      return matchesSearch && matchesRole && matchesStatus;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'name':
          aValue = a.displayName.toLowerCase();
          bValue = b.displayName.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'role':
          aValue = a.role;
          bValue = b.role;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'created':
        default:
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400';
      case 'manager': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      case 'student': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400';
      case 'pending': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      case 'suspended': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      case 'inactive': return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  const handleSort = (field: 'name' | 'email' | 'role' | 'status' | 'created') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex flex-col transition-colors duration-200 relative overflow-hidden">
        {/* Dark mode accent elements */}
        <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-200">
          <div className="absolute top-20 left-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-500/3 rounded-full blur-3xl"></div>
        </div>
        <div className="flex-1 flex flex-col overflow-y-auto bg-transparent">
          <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
            <div className="mb-5">
              <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-1.5" />
              <div className="h-3.5 w-56 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
            </div>
            <SkeletonTable rows={6} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex flex-col transition-colors duration-200 relative overflow-hidden">
      {/* Dark mode accent elements */}
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-200">
        <div className="absolute top-20 left-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-500/3 rounded-full blur-3xl"></div>
      </div>
      <div className="flex-1 flex flex-col overflow-y-auto bg-transparent">
        <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Manage and monitor all users on your platform</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>{filteredAndSortedUsers.length} users</span>
          <button
            onClick={fetchUsers}
            className="px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl text-xs font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-all duration-200"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent overflow-hidden">
        <div className="p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700/50">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 mb-2.5">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400"
              />
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as any)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              <option value="all">All Roles</option>
              <option value="student">Students</option>
              <option value="manager">Managers</option>
              <option value="admin">Admins</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              <option value="created">Created</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="role">Role</option>
              <option value="status">Status</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-2 p-3">
          {filteredAndSortedUsers.map((user) => (
            <div key={user.uid} className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3">
              <div className="flex items-center gap-2.5 mb-2">
                <img
                  src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName)}&background=3b82f6&color=fff&size=32`}
                  alt={user.displayName}
                  className="w-8 h-8 rounded-full flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{user.displayName || 'Unknown'}</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                </div>
                <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full flex-shrink-0 ${getStatusBadgeColor(user.status)}`}>
                  {user.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                  {user.role}
                </span>
                <div className="flex items-center gap-1">
                  <button className="px-2 py-1 rounded-lg text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600/50 transition-colors">
                    View
                  </button>
                  {user.status !== 'suspended' ? (
                    <button className="px-2 py-1 rounded-lg text-[11px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      Suspend
                    </button>
                  ) : (
                    <button className="px-2 py-1 rounded-lg text-[11px] text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                      Activate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/30">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button onClick={() => handleSort('name')} className="hover:text-gray-900 dark:hover:text-white">
                    User {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button onClick={() => handleSort('role')} className="hover:text-gray-900 dark:hover:text-white">
                    Role {sortBy === 'role' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button onClick={() => handleSort('status')} className="hover:text-gray-900 dark:hover:text-white">
                    Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Details</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">Last Login</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  <button onClick={() => handleSort('created')} className="hover:text-gray-900 dark:hover:text-white">
                    Created {sortBy === 'created' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </button>
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 bg-white dark:bg-transparent">
              {filteredAndSortedUsers.map((user) => (
                <tr key={user.uid} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <img
                        src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName)}&background=3b82f6&color=fff&size=32`}
                        alt={user.displayName}
                        className="h-8 w-8 rounded-full flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-gray-900 dark:text-white truncate">{user.displayName}</div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[180px]">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${getStatusBadgeColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 hidden lg:table-cell">
                    {user.role === 'student' && user.coachingData ? (
                      <div>
                        <div className="text-xs text-gray-900 dark:text-white">{user.coachingData.sessionsCompleted} sessions</div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">Score: {user.coachingData.progressScore}%</div>
                      </div>
                    ) : user.role === 'manager' ? (
                      <div className="text-xs text-gray-700 dark:text-gray-300">{user.position || '—'}</div>
                    ) : (
                      <div className="text-[11px] text-gray-400">—</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-gray-500 dark:text-gray-400 hidden xl:table-cell">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <button className="px-2 py-1 rounded-lg text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors">
                        View
                      </button>
                      {user.status !== 'suspended' ? (
                        <button className="px-2 py-1 rounded-lg text-[11px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                          Suspend
                        </button>
                      ) : (
                        <button className="px-2 py-1 rounded-lg text-[11px] text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors">
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedUsers.length === 0 && !loading && (
          <div className="text-center py-8 px-4">
            <svg className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No Users Found</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">No users found matching your criteria.</p>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;